import { useState, useEffect, useCallback } from 'react';
import { get, post, put, del } from '../api';

// Friendly short role label for sign-off footprints
function roleShort(role) {
  return { owner: 'Owner', admin: 'Manager', lead: 'Lead', staff: 'Staff' }[role] || role || '';
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Btn({ onClick, children, variant = 'primary', small, disabled, style = {} }) {
  const styles = {
    primary: { background: '#c9a96e', color: '#1a1208', border: 'none' },
    secondary: { background: 'transparent', color: '#8a7055', border: '1px solid #3a2a12' },
    danger: { background: '#e63c3c22', color: '#e63c3c', border: '1px solid #e63c3c44' },
    success: { background: '#10b98122', color: '#10b981', border: '1px solid #10b98144' },
    ghost: { background: '#c9a96e18', color: '#c9a96e', border: '1px solid #c9a96e44' },
  };
  return <button onClick={onClick} disabled={disabled} style={{ ...styles[variant], borderRadius: 8, padding: small ? '6px 14px' : '10px 22px', fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontSize: small ? 12 : 14, opacity: disabled ? 0.5 : 1, fontFamily: 'inherit', transition: 'all 0.15s', ...style }}>{children}</button>;
}

function Input({ label, value, onChange, placeholder, type = 'text', rows }) {
  const s = { width: '100%', background: '#2a1f0f', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 14px', color: '#f5e6c8', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>}
      {rows ? <textarea value={value} onChange={onChange} placeholder={placeholder} rows={rows} style={{ ...s, resize: 'vertical' }} /> : <input type={type} value={value} onChange={onChange} placeholder={placeholder} style={s} />}
    </div>
  );
}

function Modal({ children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 14, padding: 32, maxWidth: wide ? 860 : 660, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}

// ── Announcements ─────────────────────────────────────────────────────────────
export function AnnouncementsView({ isManager }) {
  const [announcements, setAnnouncements] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', priority: 'normal' });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setAnnouncements(await get('/announcements')); } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function addAnnouncement() {
    if (!form.title || !form.body) return;
    await post('/announcements', form);
    setForm({ title: '', body: '', priority: 'normal' });
    setShowAdd(false);
    load();
  }

  async function deleteAnn(id) {
    await del(`/announcements/${id}`);
    load();
  }

  const priorityColor = { high: '#e63c3c', normal: '#c9a96e', info: '#6366f1' };
  const priorityIcon = { high: '🚨', normal: '📢', info: 'ℹ️' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 4 }}>Announcements</h2>
          <p style={{ color: '#8a7055', fontSize: 13 }}>Important updates from management</p>
        </div>
        {isManager && <Btn onClick={() => setShowAdd(true)}>+ Post Announcement</Btn>}
      </div>

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 20 }}>Post Announcement</h3>
          <Input label="Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Updated axe safety rules effective Monday" />
          <Input label="Message" value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))} placeholder="Write your announcement here..." rows={4} />
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Priority</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {[{ v: 'normal', label: '📢 Normal' }, { v: 'high', label: '🚨 Urgent' }, { v: 'info', label: 'ℹ️ Info' }].map(o => (
                <div key={o.v} onClick={() => setForm(p => ({ ...p, priority: o.v }))} style={{ padding: 10, borderRadius: 8, cursor: 'pointer', border: `1px solid ${form.priority === o.v ? priorityColor[o.v] : '#3a2a12'}`, background: form.priority === o.v ? priorityColor[o.v] + '18' : '#2a1f0f', color: form.priority === o.v ? priorityColor[o.v] : '#8a7055', textAlign: 'center', fontSize: 13 }}>{o.label}</div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={addAnnouncement} disabled={!form.title || !form.body}>Post</Btn>
            <Btn variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {loading && <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>Loading...</p>}
      {!loading && announcements.length === 0 && <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>No announcements yet.</p>}

      <div style={{ display: 'grid', gap: 14 }}>
        {announcements.map(a => (
          <div key={a.id} style={{ background: '#1a1208', border: `1px solid ${priorityColor[a.priority] || '#3a2a12'}44`, borderRadius: 12, padding: '18px 22px', borderLeft: `4px solid ${priorityColor[a.priority] || '#c9a96e'}` }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span>{priorityIcon[a.priority] || '📢'}</span>
                  <span style={{ color: '#f5e6c8', fontWeight: 700, fontSize: 16 }}>{a.title}</span>
                </div>
                <p style={{ color: '#d4b896', fontSize: 14, lineHeight: 1.6, marginBottom: 8 }}>{a.body}</p>
                <span style={{ color: '#5a4030', fontSize: 11 }}>Posted by {a.created_by} · {new Date(a.created_at).toLocaleDateString()}</span>
              </div>
              {isManager && <Btn small variant="danger" onClick={() => deleteAnn(a.id)}>Remove</Btn>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Onboarding Checklist ──────────────────────────────────────────────────────
export function OnboardingView({ isManager, userId, isOwner }) {
  const [templates, setTemplates] = useState([]);
  const [myProgress, setMyProgress] = useState([]);
  const [allProgress, setAllProgress] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [t, mp] = await Promise.all([get('/onboarding/templates'), get('/onboarding/my')]);
      setTemplates(t);
      setMyProgress(mp);
      if (isManager) {
        const ap = await get('/onboarding/all');
        setAllProgress(ap);
      }
    } catch {} finally { setLoading(false); }
  }, [isManager]);

  useEffect(() => { load(); }, [load]);

  async function toggleItem(templateId, itemId, completed) {
    await post('/onboarding/progress', { template_id: templateId, item_id: itemId, completed });
    load();
  }

  const [expandedStaff, setExpandedStaff] = useState(null);

  async function signOffDay(user_id, template_id, day) {
    await post('/onboarding/day-signoff', { user_id, template_id, day });
    load();
  }
  async function unSignDay(user_id, template_id, day) {
    await post('/onboarding/day-signoff/remove', { user_id, template_id, day });
    load();
  }
  async function finalSignOff(user_id, template_id) {
    if (!window.confirm('Finalize this employee\'s onboarding? This records your name and the date as the approving manager.')) return;
    try { await post('/onboarding/final-signoff', { user_id, template_id }); load(); }
    catch (e) { alert(e.message); }
  }
  async function undoFinal(user_id, template_id) {
    await post('/onboarding/final-signoff/remove', { user_id, template_id });
    load();
  }

  function getProgress(templateId) {
    return myProgress.find(p => p.template_id === templateId) || { completed_items: [] };
  }

  if (loading) return <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>Loading...</p>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 4 }}>Onboarding</h2>
          <p style={{ color: '#8a7055', fontSize: 13 }}>Your 5-day training schedule</p>
        </div>
        {isManager && <Btn onClick={() => { setEditingTemplate(null); setShowEditor(true); }}>+ New Schedule</Btn>}
      </div>

      {showEditor && <TemplateEditor template={editingTemplate} isOwner={isOwner} onClose={() => { setShowEditor(false); setEditingTemplate(null); }} onSave={load} />}

      {templates.map(t => {
        const prog = getProgress(t.id);
        const completedItems = prog.completed_items || [];
        const totalItems = t.days.reduce((acc, d) => acc + d.items.length, 0);
        const completedCount = completedItems.length;
        const pct = totalItems ? Math.round((completedCount / totalItems) * 100) : 0;

        return (
          <div key={t.id} style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #3a2a12' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', fontSize: 20, marginBottom: 4 }}>{t.title}</h3>
                  <p style={{ color: '#8a7055', fontSize: 13 }}>{t.description}</p>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 20 }}>
                  <div style={{ color: '#c9a96e', fontWeight: 700, fontSize: 22 }}>{pct}%</div>
                  <div style={{ color: '#5a4030', fontSize: 11 }}>{completedCount}/{totalItems} items</div>
                </div>
              </div>
              <div style={{ background: '#2a1f0f', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#10b981' : '#c9a96e', borderRadius: 4, transition: 'width 0.4s ease' }} />
              </div>
              {isManager && (
                <div style={{ marginTop: 12 }}>
                  <Btn small variant="ghost" onClick={() => { setEditingTemplate(t); setShowEditor(true); }}>✏️ Edit Schedule</Btn>
                </div>
              )}
            </div>

            {t.days.map(day => {
              const dayCompleted = day.items.filter(i => completedItems.includes(i.id)).length;
              return (
                <div key={day.day} style={{ borderBottom: '1px solid #2a1f0f' }}>
                  <div style={{ padding: '14px 24px', background: '#1e1508', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ color: '#c9a96e', fontWeight: 700, fontSize: 14 }}>{day.title}</span>
                    <span style={{ color: '#5a4030', fontSize: 12 }}>{dayCompleted}/{day.items.length}</span>
                  </div>
                  <div style={{ padding: '8px 24px 16px' }}>
                    {day.items.map(item => {
                      const done = completedItems.includes(item.id);
                      return (
                        <div key={item.id} onClick={() => toggleItem(t.id, item.id, !done)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', cursor: 'pointer', borderBottom: '1px solid #2a1f0f' }}>
                          <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${done ? '#10b981' : '#3a2a12'}`, background: done ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.15s' }}>
                            {done && <span style={{ color: '#1a1208', fontSize: 13, fontWeight: 700 }}>✓</span>}
                          </div>
                          <span style={{ color: done ? '#10b981' : '#d4b896', fontSize: 14, textDecoration: done ? 'line-through' : 'none', opacity: done ? 0.7 : 1, flex: 1 }}>{item.label}</span>
                          {item.required && !done && <span style={{ color: '#5a4030', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Required</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {isManager && allProgress.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#c9a96e', marginBottom: 16 }}>Staff Onboarding Progress</h3>
          <div style={{ display: 'grid', gap: 10 }}>
            {allProgress.map(p => {
              const template = templates.find(t => t.id === p.template_id);
              const totalItems = template?.days?.reduce((acc, d) => acc + d.items.length, 0) || 0;
              const pct = totalItems ? Math.round((p.completed_items.length / totalItems) * 100) : 0;
              const isExpanded = expandedStaff === p.id;
              const signoffs = p.day_signoffs || {};
              return (
                <div key={p.id} style={{ background: '#1a1208', border: `1px solid ${p.final_signoff_by ? '#10b98144' : '#3a2a12'}`, borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }} onClick={() => setExpandedStaff(isExpanded ? null : p.id)}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#f5e6c8', fontWeight: 600 }}>{p.user_name} {p.final_signoff_by && <span style={{ color: '#10b981', fontSize: 12 }}>✓ Fully Onboarded</span>}</div>
                      <div style={{ color: '#5a4030', fontSize: 12 }}>{p.position} · {p.template_title}</div>
                      <div style={{ marginTop: 8, background: '#2a1f0f', borderRadius: 4, height: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#10b981' : '#c9a96e', borderRadius: 4 }} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ color: pct === 100 ? '#10b981' : '#c9a96e', fontWeight: 700 }}>{pct}%</div>
                      <div style={{ color: '#5a4030', fontSize: 11 }}>{p.completed_items.length}/{totalItems}</div>
                    </div>
                    <span style={{ color: '#5a4030', fontSize: 16 }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>

                  {isExpanded && template && (
                    <div style={{ borderTop: '1px solid #3a2a12', padding: '16px 18px' }}>
                      <div style={{ color: '#c9a96e', fontSize: 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Per-Day Sign-Off</div>
                      <div style={{ display: 'grid', gap: 8, marginBottom: 18 }}>
                        {template.days.map((d, di) => {
                          const dayKey = String(d.day ?? di + 1);
                          const so = signoffs[dayKey];
                          return (
                            <div key={dayKey} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#2a1f0f', borderRadius: 8 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ color: '#f5e6c8', fontSize: 13, fontWeight: 600 }}>{d.title || `Day ${dayKey}`}</div>
                                {so
                                  ? <div style={{ color: '#10b981', fontSize: 11, marginTop: 2 }}>✓ Signed off by {so.by} ({roleShort(so.role)}) · {new Date(so.at).toLocaleDateString()}</div>
                                  : <div style={{ color: '#5a4030', fontSize: 11, marginTop: 2 }}>Not yet signed off</div>}
                              </div>
                              {so
                                ? <Btn small variant="secondary" onClick={() => unSignDay(p.user_id, p.template_id, dayKey)}>Undo</Btn>
                                : <Btn small variant="success" onClick={() => signOffDay(p.user_id, p.template_id, dayKey)}>Sign Off</Btn>}
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ borderTop: '1px solid #3a2a12', paddingTop: 14 }}>
                        {(() => {
                          const isKitchen = /kitchen|boh|food runner|cook|chef|dish/i.test(p.position || '');
                          const missing = [];
                          if (!p.food_handler_cert) missing.push('Food Handler cert');
                          if (!p.harassment_cert) missing.push('Harassment cert');
                          if (!isKitchen && !p.rbs_number) missing.push('RBS number');
                          if (!p.final_signoff_by && missing.length) {
                            return (
                              <div style={{ background: '#f59e0b14', border: '1px solid #f59e0b44', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>
                                <div style={{ color: '#f59e0b', fontSize: 12, fontWeight: 700 }}>⚠️ Required before finalizing:</div>
                                <div style={{ color: '#d4b896', fontSize: 12, marginTop: 4 }}>Missing {missing.join(', ')}. Upload on their Staff Profile first.</div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {p.final_signoff_by ? (
                          <div style={{ background: '#10b98114', border: '1px solid #10b98144', borderRadius: 8, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                              <div style={{ color: '#10b981', fontWeight: 700, fontSize: 13 }}>✓ Onboarding Complete</div>
                              <div style={{ color: '#8a7055', fontSize: 11, marginTop: 2 }}>Finalized by {p.final_signoff_by} ({roleShort(p.final_signoff_role)}) on {p.final_signoff_at ? new Date(p.final_signoff_at).toLocaleString() : '—'}</div>
                            </div>
                            <Btn small variant="secondary" onClick={() => undoFinal(p.user_id, p.template_id)}>Undo</Btn>
                          </div>
                        ) : (
                          <Btn variant="success" onClick={() => finalSignOff(p.user_id, p.template_id)}>✓ Finalize Onboarding (records your name & date)</Btn>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ template, onClose, onSave, isOwner }) {
  const isNew = !template;
  const [form, setForm] = useState({
    title: template?.title || '',
    description: template?.description || '',
    days: template?.days || [{ day: 1, title: 'Day 1', items: [] }],
  });
  const [loading, setLoading] = useState(false);

  async function save() {
    setLoading(true);
    try {
      if (isNew) {
        await post('/onboarding/templates', form);
      } else {
        const res = await put(`/onboarding/templates/${template.id}`, form);
        if (res && res.requested) {
          alert('✓ Your changes were submitted to the owner for approval. They won\'t go live until approved. You can edit or cancel the request in the Change Requests tab.');
          onSave(); onClose();
          return;
        }
      }
      onSave(); onClose();
    } catch (e) { alert(e.message); }
    finally { setLoading(false); }
  }

  function addDay() {
    const nextDay = form.days.length + 1;
    setForm(p => ({ ...p, days: [...p.days, { day: nextDay, title: `Day ${nextDay}`, items: [] }] }));
  }

  function addItem(dayIdx) {
    const days = [...form.days];
    const id = `d${dayIdx + 1}-${Date.now()}`;
    days[dayIdx].items.push({ id, label: '', required: true });
    setForm(p => ({ ...p, days }));
  }

  function updateItem(dayIdx, itemIdx, label) {
    const days = [...form.days];
    days[dayIdx].items[itemIdx].label = label;
    setForm(p => ({ ...p, days }));
  }

  function removeItem(dayIdx, itemIdx) {
    const days = [...form.days];
    days[dayIdx].items.splice(itemIdx, 1);
    setForm(p => ({ ...p, days }));
  }

  return (
    <Modal onClose={onClose} wide>
      <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 20 }}>{isNew ? 'Create Onboarding Schedule' : 'Edit Schedule'}</h3>
      <Input label="Schedule Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Bar Staff Onboarding" />
      <Input label="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief overview..." rows={2} />

      {form.days.map((day, di) => (
        <div key={di} style={{ background: '#2a1f0f', borderRadius: 10, padding: 16, marginBottom: 14 }}>
          <input value={day.title} onChange={e => { const days = [...form.days]; days[di].title = e.target.value; setForm(p => ({ ...p, days })); }} style={{ background: 'transparent', border: 'none', color: '#c9a96e', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', width: '100%', marginBottom: 10 }} />
          {day.items.map((item, ii) => (
            <div key={ii} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
              <input value={item.label} onChange={e => updateItem(di, ii, e.target.value)} placeholder="Checklist item..." style={{ flex: 1, background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 6, padding: '7px 10px', color: '#f5e6c8', fontSize: 13, fontFamily: 'inherit' }} />
              <button onClick={() => removeItem(di, ii)} style={{ background: 'none', border: 'none', color: '#e63c3c', cursor: 'pointer', fontSize: 18 }}>×</button>
            </div>
          ))}
          <button onClick={() => addItem(di)} style={{ background: 'none', border: '1px dashed #3a2a12', borderRadius: 6, color: '#8a7055', padding: '5px 12px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit', marginTop: 4 }}>+ Add Item</button>
        </div>
      ))}
      <Btn small variant="secondary" onClick={addDay} style={{ marginBottom: 20 }}>+ Add Day</Btn>

      <div style={{ display: 'flex', gap: 10 }}>
        <Btn onClick={save} disabled={loading || !form.title}>{loading ? 'Saving...' : 'Save Schedule'}</Btn>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
}

// ── Acknowledgments ───────────────────────────────────────────────────────────
export function AcknowledgmentsView({ isManager, isGM }) {
  const [docs, setDocs] = useState([]);
  const [myAcks, setMyAcks] = useState([]);
  const [allAcks, setAllAcks] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', required: 1 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [d, ma] = await Promise.all([get('/acknowledgments/docs'), get('/acknowledgments/my')]);
      setDocs(d); setMyAcks(ma);
      if (isGM) { const aa = await get('/acknowledgments/all'); setAllAcks(aa); }
    } catch {} finally { setLoading(false); }
  }, [isGM]);

  useEffect(() => { load(); }, [load]);

  async function sign(docId) {
    await post('/acknowledgments/sign', { doc_id: docId });
    setViewing(null);
    load();
  }

  async function addDoc() {
    await post('/acknowledgments/docs', form);
    setShowAdd(false);
    setForm({ title: '', content: '', required: 1 });
    load();
  }

  const signed = (docId) => myAcks.some(a => a.doc_id === docId);
  const unsignedRequired = docs.filter(d => d.required && !signed(d.id));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 4 }}>Acknowledgments</h2>
          <p style={{ color: '#8a7055', fontSize: 13 }}>Digital sign-offs required before working</p>
        </div>
        {isManager && <Btn onClick={() => setShowAdd(true)}>+ Add Document</Btn>}
      </div>

      {unsignedRequired.length > 0 && (
        <div style={{ background: '#e63c3c18', border: '1px solid #e63c3c44', borderRadius: 10, padding: 16, marginBottom: 24 }}>
          <div style={{ color: '#e63c3c', fontWeight: 700, marginBottom: 6 }}>⚠️ Action Required</div>
          <div style={{ color: '#d4b896', fontSize: 13 }}>{unsignedRequired.length} required acknowledgment{unsignedRequired.length !== 1 ? 's' : ''} need your signature before you can work.</div>
        </div>
      )}

      {viewing && (
        <Modal onClose={() => setViewing(null)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 16 }}>{viewing.title}</h3>
          <div style={{ background: '#2a1f0f', borderRadius: 10, padding: 20, marginBottom: 20 }}>
            <p style={{ color: '#d4b896', lineHeight: 1.7, fontSize: 14 }}>{viewing.content}</p>
          </div>
          {!signed(viewing.id) ? (
            <>
              <p style={{ color: '#8a7055', fontSize: 13, marginBottom: 16 }}>By clicking below you confirm you have read and understood this document.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <Btn variant="success" onClick={() => sign(viewing.id)}>✓ I Acknowledge & Sign</Btn>
                <Btn variant="secondary" onClick={() => setViewing(null)}>Close</Btn>
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#10b981', fontWeight: 700 }}>✓ Signed</span>
              <Btn variant="secondary" onClick={() => setViewing(null)}>Close</Btn>
            </div>
          )}
        </Modal>
      )}

      {showAdd && isManager && (
        <Modal onClose={() => setShowAdd(false)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 20 }}>Add Acknowledgment Document</h3>
          <Input label="Document Title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Updated Alcohol Service Policy" />
          <Input label="Document Text (what staff will sign)" value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="I confirm that I have read and understood..." rows={6} />
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Required?</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[{ v: 1, label: 'Yes — required before working' }, { v: 0, label: 'No — optional' }].map(o => (
                <div key={o.v} onClick={() => setForm(p => ({ ...p, required: o.v }))} style={{ padding: 10, borderRadius: 8, cursor: 'pointer', border: `1px solid ${form.required === o.v ? '#c9a96e' : '#3a2a12'}`, background: form.required === o.v ? '#c9a96e18' : '#2a1f0f', color: form.required === o.v ? '#f5e6c8' : '#8a7055', textAlign: 'center', fontSize: 13 }}>{o.label}</div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={addDoc} disabled={!form.title || !form.content}>Add Document</Btn>
            <Btn variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      <div style={{ display: 'grid', gap: 12, marginBottom: 32 }}>
        {docs.map(d => {
          const isSigned = signed(d.id);
          return (
            <div key={d.id} style={{ background: '#1a1208', border: `1px solid ${isSigned ? '#10b98144' : d.required ? '#e63c3c44' : '#3a2a12'}`, borderRadius: 10, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ fontSize: 24 }}>{isSigned ? '✅' : d.required ? '📋' : '📄'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: '#f5e6c8', fontWeight: 600 }}>{d.title}</div>
                <div style={{ color: '#5a4030', fontSize: 12 }}>{d.required ? 'Required' : 'Optional'} · {isSigned ? `Signed ✓` : 'Needs your signature'}</div>
              </div>
              <Btn small variant={isSigned ? 'secondary' : 'primary'} onClick={() => setViewing(d)}>
                {isSigned ? 'View' : 'Read & Sign'}
              </Btn>
            </div>
          );
        })}
      </div>

      {isGM && allAcks.length > 0 && (
        <div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#c9a96e', marginBottom: 16 }}>Staff Signatures</h3>
          <div style={{ display: 'grid', gap: 8 }}>
            {allAcks.map(a => (
              <div key={a.id} style={{ background: '#1a1208', border: '1px solid #3a2a12', borderRadius: 8, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ color: '#10b981' }}>✓</span>
                <span style={{ color: '#f5e6c8', fontSize: 13, flex: 1 }}>{a.user_name}</span>
                <span style={{ color: '#8a7055', fontSize: 12 }}>{a.doc_title}</span>
                <span style={{ color: '#5a4030', fontSize: 11 }}>{new Date(a.signed_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Incidents & Shift Notes ───────────────────────────────────────────────────
export function IncidentsView({ isManager, isOwner, isGM }) {
  const [incidents, setIncidents] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', incident_type: 'shift_note', location: '', shift_date: new Date().toISOString().split('T')[0], manager_notified: false });
  const [resolveModal, setResolveModal] = useState(null);
  const [resolveNotes, setResolveNotes] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const path = isManager ? '/incidents' : '/incidents/my';
      setIncidents(await get(path));
    } catch {} finally { setLoading(false); }
  }, [isManager]);

  useEffect(() => { load(); }, [load]);

  async function submit() {
    if (!form.title || !form.description) return;
    await post('/incidents', form);
    setShowAdd(false);
    setForm({ title: '', description: '', incident_type: 'shift_note', location: '', shift_date: new Date().toISOString().split('T')[0], manager_notified: false });
    load();
  }

  async function resolve() {
    await put(`/incidents/${resolveModal.id}/resolve`, { manager_notes: resolveNotes });
    setResolveModal(null); setResolveNotes('');
    load();
  }

  async function deleteIncident(id) {
    if (!window.confirm('Delete this report permanently? This cannot be undone.')) return;
    await del(`/incidents/${id}`);
    load();
  }

  const typeColor = { shift_note: '#6366f1', incident: '#e63c3c', safety: '#f59e0b', maintenance: '#10b981' };
  const typeLabel = { shift_note: 'Shift Note', incident: 'Incident Report', safety: 'Safety Concern', maintenance: 'Maintenance Issue' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, color: '#f5e6c8', marginBottom: 4 }}>
            {isManager ? 'Incident Log & Shift Notes' : 'My Shift Notes'}
          </h2>
          <p style={{ color: '#8a7055', fontSize: 13 }}>Log shift notes, incidents, safety concerns, and maintenance issues</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isGM && <a href={`/api/export/incidents?token=${localStorage.getItem('tnt_token')}`} target="_blank" rel="noreferrer" style={{ background: '#6366f122', color: '#6366f1', border: '1px solid #6366f144', borderRadius: 8, padding: '10px 16px', textDecoration: 'none', fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center' }}>📋 Export CSV</a>}
          <Btn onClick={() => setShowAdd(true)}>+ Submit Report</Btn>
        </div>
      </div>

      {showAdd && (
        <Modal onClose={() => setShowAdd(false)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 20 }}>Submit Report</h3>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', color: '#8a7055', fontSize: 11, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Report Type</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {Object.entries(typeLabel).map(([v, label]) => (
                <div key={v} onClick={() => setForm(p => ({ ...p, incident_type: v }))} style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${form.incident_type === v ? typeColor[v] : '#3a2a12'}`, background: form.incident_type === v ? typeColor[v] + '18' : '#2a1f0f', color: form.incident_type === v ? typeColor[v] : '#8a7055', textAlign: 'center', fontSize: 12, fontWeight: form.incident_type === v ? 700 : 400 }}>{label}</div>
              ))}
            </div>
          </div>
          <Input label="Title / Summary" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Guest complaint about wait time at axe lane 3" />
          <Input label="Full Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe what happened, who was involved, and what action was taken..." rows={4} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Location (optional)" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Axe Lane 3" />
            <Input label="Shift Date" type="date" value={form.shift_date} onChange={e => setForm(p => ({ ...p, shift_date: e.target.value }))} />
          </div>
          <div onClick={() => setForm(p => ({ ...p, manager_notified: !p.manager_notified }))} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 20 }}>
            <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${form.manager_notified ? '#10b981' : '#3a2a12'}`, background: form.manager_notified ? '#10b981' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {form.manager_notified && <span style={{ color: '#1a1208', fontSize: 13, fontWeight: 700 }}>✓</span>}
            </div>
            <span style={{ color: '#d4b896', fontSize: 13 }}>Manager was notified in person during shift</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn onClick={submit} disabled={!form.title || !form.description}>Submit Report</Btn>
            <Btn variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {resolveModal && isManager && (
        <Modal onClose={() => setResolveModal(null)}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#f5e6c8', marginBottom: 6 }}>Resolve Report</h3>
          <p style={{ color: '#8a7055', marginBottom: 20 }}>{resolveModal.title}</p>
          <Input label="Manager Notes (optional)" value={resolveNotes} onChange={e => setResolveNotes(e.target.value)} placeholder="How was this resolved?" rows={3} />
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="success" onClick={resolve}>✓ Mark Resolved</Btn>
            <Btn variant="secondary" onClick={() => setResolveModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {loading && <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>Loading...</p>}
      {!loading && incidents.length === 0 && <p style={{ color: '#5a4030', textAlign: 'center', padding: 40 }}>No reports yet.</p>}

      <div style={{ display: 'grid', gap: 12 }}>
        {incidents.map(inc => (
          <div key={inc.id} style={{ background: '#1a1208', border: `1px solid ${inc.resolved ? '#10b98133' : typeColor[inc.incident_type] + '44'}`, borderRadius: 10, padding: '16px 20px', opacity: inc.resolved ? 0.7 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ background: typeColor[inc.incident_type] + '22', color: typeColor[inc.incident_type], border: `1px solid ${typeColor[inc.incident_type]}44`, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>{typeLabel[inc.incident_type]}</span>
                  {inc.resolved && <span style={{ background: '#10b98122', color: '#10b981', border: '1px solid #10b98144', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>✓ Resolved</span>}
                </div>
                <div style={{ color: '#f5e6c8', fontWeight: 600, marginBottom: 4 }}>{inc.title}</div>
                <div style={{ color: '#8a7055', fontSize: 13, lineHeight: 1.5 }}>{inc.description}</div>
                {inc.manager_notes && <div style={{ color: '#10b981', fontSize: 12, marginTop: 8 }}>Manager: {inc.manager_notes}</div>}
              </div>
              {isManager && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {!inc.resolved && <Btn small variant="success" onClick={() => { setResolveModal(inc); setResolveNotes(''); }}>Resolve</Btn>}
                  {isOwner && <Btn small variant="danger" onClick={() => deleteIncident(inc.id)}>Delete</Btn>}
                </div>
              )}
            </div>
            <div style={{ color: '#5a4030', fontSize: 11, borderTop: '1px solid #2a1f0f', paddingTop: 8, marginTop: 8 }}>
              {isManager && <span>{inc.user_name} · </span>}
              {inc.location && <span>{inc.location} · </span>}
              {inc.shift_date} · {inc.manager_notified ? 'Manager notified' : 'Manager not notified during shift'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Certification Expiry Banner ────────────────────────────────────────────────
export function ExpiryBanner({ modules, myProgress }) {
  const expiring = modules.filter(m => {
    if (!m.cert_expiry_days) return false;
    const prog = myProgress.find(p => p.module_id === m.id);
    if (!prog?.certified_at) return false;
    const certDate = new Date(prog.certified_at);
    const expiryDate = new Date(certDate.getTime() + m.cert_expiry_days * 24 * 60 * 60 * 1000);
    const daysLeft = Math.floor((expiryDate - new Date()) / (24 * 60 * 60 * 1000));
    return daysLeft < 30;
  });

  if (expiring.length === 0) return null;

  return (
    <div style={{ background: '#f59e0b18', border: '1px solid #f59e0b44', borderRadius: 10, padding: 16, marginBottom: 20 }}>
      <div style={{ color: '#f59e0b', fontWeight: 700, marginBottom: 6 }}>⏰ Certification Renewal Required</div>
      {expiring.map(m => {
        const prog = myProgress.find(p => p.module_id === m.id);
        const certDate = new Date(prog.certified_at);
        const expiryDate = new Date(certDate.getTime() + m.cert_expiry_days * 24 * 60 * 60 * 1000);
        const daysLeft = Math.floor((expiryDate - new Date()) / (24 * 60 * 60 * 1000));
        return (
          <div key={m.id} style={{ color: '#d4b896', fontSize: 13 }}>
            • {m.title} — {daysLeft <= 0 ? 'EXPIRED' : `expires in ${daysLeft} days`}
          </div>
        );
      })}
    </div>
  );
}
